import { BigNumber } from '@ethersproject/bignumber'
import type { TransactionRequest } from '@ethersproject/providers'
import { Web3Provider } from '@ethersproject/providers'
import { t } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { useAccountDrawer } from 'components/AccountDrawer'
import Column from 'components/Column'
import { getYear, isSameDay, isSameMonth, isSameWeek, isSameYear } from 'date-fns'
import { TransactionStatus } from 'graphql/data/__generated__/types-and-hooks'
import { atom, useAtom } from 'jotai'
import { EmptyWalletModule } from 'nft/components/profile/view/EmptyWalletContent'
import { useCallback, useEffect, useMemo, useState } from 'react'
import store from 'state'
import { useAppDispatch } from 'state/hooks'
import { addTransaction } from 'state/transactions/reducer'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'

import { PortfolioTabWrapper } from '../PortfolioRow'
import { ActivityRow } from './ActivityRow'
import { useLocalActivities } from './parseLocal'
import { Activity } from './types'

interface ActivityGroup {
  title: string
  transactions: Array<Activity>
}

const sortActivities = (a: Activity, b: Activity) => Number(b?.timestamp ?? 0) - Number(a?.timestamp ?? 0)

const createGroups = (activities?: Array<Activity>) => {
  if (!activities || !activities.length) return []
  const now = Date.now()

  const pending: Array<Activity> = []
  const today: Array<Activity> = []
  const currentWeek: Array<Activity> = []
  const last30Days: Array<Activity> = []
  const currentYear: Array<Activity> = []
  const yearMap: { [key: string]: Array<Activity> } = {}

  activities.forEach((activity) => {
    if (activity.status === TransactionStatus.Pending) {
      pending.push(activity)
      return
    }
    const addedTime = Number(activity?.timestamp ?? 0) * 1000
    if (isSameDay(now, addedTime)) today.push(activity)
    else if (isSameWeek(addedTime, now)) currentWeek.push(activity)
    else if (isSameMonth(addedTime, now)) last30Days.push(activity)
    else if (isSameYear(addedTime, now)) currentYear.push(activity)
    else {
      const year = getYear(addedTime)
      if (!yearMap[year]) yearMap[year] = [activity]
      else yearMap[year].push(activity)
    }
  })

  const sortedYears = Object.keys(yearMap)
    .sort((a, b) => parseInt(b) - parseInt(a))
    .map((year) => ({ title: year, transactions: yearMap[year] }))

  const transactionGroups: Array<ActivityGroup> = [
    { title: t`Pending`, transactions: pending.sort(sortActivities) },
    { title: t`Today`, transactions: today.sort(sortActivities) },
    { title: t`This week`, transactions: currentWeek.sort(sortActivities) },
    { title: t`This month`, transactions: last30Days.sort(sortActivities) },
    { title: t`This year`, transactions: currentYear.sort(sortActivities) },
    ...sortedYears,
  ]

  return transactionGroups.filter((g) => g.transactions.length > 0)
}

const ActivityGroupWrapper = styled(Column)`
  margin-top: 0px;
  gap: 4px;
`

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  min-height: 32px;
`

const ClearBtn = styled.button`
  font-size: 12px;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 30px;
  background: ${({ theme }) => theme.backgroundInteractive};
  color: ${({ theme }) => theme.textPrimary};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${({ theme }) => theme.backgroundSurface};
  }
`

const lastFetchedAtom = atom<number | undefined>(0)

const clearedKey = (account: string) => `piteas:recent_swaps:clearedAt:v1:${account.toLowerCase()}`
const readClearedAt = (account: string | undefined) => {
  if (!account) return 0
  try {
    const raw = localStorage.getItem(clearedKey(account))
    return raw ? parseInt(raw) || 0 : 0
  } catch {
    return 0
  }
}
const writeClearedAt = (account: string | undefined, value: number) => {
  if (!account) return
  try {
    localStorage.setItem(clearedKey(account), String(value))
  } catch {
    // Silent fail
  }
}

function useStableProvider(connector: any) {
  const [provider, setProvider] = useState<Web3Provider | null>(null)
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const injected: any = (window as any).ethereum
        if (injected) {
          if (!alive) return
          setProvider(new Web3Provider(injected, 'any'))
          return
        }
        const prov = (await connector?.getProvider?.()) || connector?.provider
        if (prov && alive) setProvider(new Web3Provider(prov, 'any'))
      } catch {
        // silent
      }
    })()
    return () => {
      alive = false
    }
  }, [connector])
  return provider
}

const bump25 = (v?: BigNumber | null) => (v ? v.mul(150).div(100) : undefined)

const replacementsKey = (account: string) => `piteas:tx_replacements:v1:${account.toLowerCase()}`
function readReplacements(account?: string): Record<string, string> {
  if (!account) return {}
  try {
    const raw = localStorage.getItem(replacementsKey(account))
    return raw ? (JSON.parse(raw) as Record<string, string>) : {}
  } catch {
    return {}
  }
}
function writeReplacement(account: string | undefined, oldHash: string, newHash: string) {
  if (!account || !oldHash || !newHash) return
  try {
    const map = readReplacements(account)
    map[oldHash.toLowerCase()] = newHash.toLowerCase()
    localStorage.setItem(replacementsKey(account), JSON.stringify(map))
  } catch {
    // Silent fail
  }
}

/** old -> h1 -> h2 -> ... -> final */
function followReplacement(hash: string | undefined, map: Record<string, string> = {}): string | undefined {
  if (!hash) return undefined
  let cur = hash.toLowerCase()
  const seen = new Set<string>()
  while (map[cur] && !seen.has(cur)) {
    seen.add(cur)
    cur = map[cur].toLowerCase()
  }
  return cur
}

export function ActivityTab({ account }: { account: string }) {
  const [isDrawerOpen, toggleWalletDrawer] = useAccountDrawer()
  const [lastFetched, setLastFetched] = useAtom(lastFetchedAtom)
  const [clearedAt, setClearedAt] = useState<number>(() => readClearedAt(account))
  const dispatch = useAppDispatch()

  const localMap = useLocalActivities(account)

  const rawLocalSwapList: Activity[] = useMemo(() => {
    const vals = Object.values(localMap ?? {}) as Activity[]
    return vals
      .filter((a) => {
        const title = (a?.title ?? '').toLowerCase()
        const isSwap = title.startsWith('swap')
        const isWrap = title.startsWith('wrap') || title.startsWith('unwrap')
        const isApproval = title.startsWith('approv') || title.startsWith('revok')
        const isCancelled = title === 'cancelled'
        const isAfterClear = Number(a?.timestamp ?? 0) * 1000 > (clearedAt || 0)
        const isPLS = (a?.chainId ?? 0) === 369
        return (isSwap || isWrap || isApproval || isCancelled) && isPLS && isAfterClear
      })
      .sort(sortActivities)
  }, [localMap, clearedAt])

  useEffect(() => {
    if (!lastFetched) setLastFetched(Date.now())
  }, [isDrawerOpen, lastFetched, setLastFetched])

  const { connector, chainId } = useWeb3React()
  const ethersProvider = useStableProvider(connector)

  const [replacements, setReplacements] = useState<Record<string, string>>(() => readReplacements(account))
  useEffect(() => {
    setReplacements(readReplacements(account))
  }, [account])

  const [nonceMap, setNonceMap] = useState<Record<string, number>>({})
  const [confirmedSet, setConfirmedSet] = useState<Record<string, true>>({})

  const displayList: Activity[] = useMemo(() => {
    const reverseLookup: Record<string, boolean> = {}
    Object.values(replacements).forEach((newHash) => {
      reverseLookup[newHash.toLowerCase()] = true
    })

    const withFinalHash = rawLocalSwapList.map((a) => {
      const finalHash = followReplacement(a.hash, replacements)
      const wasReplaced = finalHash && finalHash !== a.hash
      const isReplacementTx = reverseLookup[a.hash?.toLowerCase() ?? '']

      if (wasReplaced) {
        return { ...a, hash: finalHash, isSpedUp: true }
      } else if (isReplacementTx) {
        return { ...a, isSpedUp: true }
      }
      return a
    })

    const seen = new Set<string>()
    const deduped: Activity[] = []
    for (const a of withFinalHash) {
      const key = a.hash?.toLowerCase() || `${Number(a?.timestamp ?? 0)}:${a?.title ?? ''}`
      if (!seen.has(key)) {
        if (key) seen.add(key)
        deduped.push(a)
      }
    }

    return deduped.map((a) => {
      const confirmed = confirmedSet[a.hash?.toLowerCase() ?? '']
      return confirmed ? { ...a, status: TransactionStatus.Confirmed } : a
    })
  }, [rawLocalSwapList, replacements, confirmedSet])

  const pendingList = useMemo(() => displayList.filter((a) => a.status === TransactionStatus.Pending), [displayList])

  const speedUpCandidateHash = useMemo(() => {
    const pendings = pendingList.slice()
    if (!pendings.length) return undefined

    const withNonce = pendings
      .map((p) => ({ p, n: nonceMap[p.hash?.toLowerCase() ?? ''] }))
      .filter((x) => Number.isFinite(x.n))
      .sort((a, b) => (a.n as number) - (b.n as number))
    if (withNonce.length) return withNonce[0].p.hash?.toLowerCase()

    pendings.sort(
      (a, b) => Number(a?.timestamp ?? Number.MAX_SAFE_INTEGER) - Number(b?.timestamp ?? Number.MAX_SAFE_INTEGER)
    )
    return pendings[0]?.hash?.toLowerCase()
  }, [pendingList, nonceMap])

  const onSpeedUp = useCallback(
    async (oldHash?: string) => {
      if (!ethersProvider || !account || !oldHash || !chainId) return
      try {
        const signer = ethersProvider.getSigner()
        const oldH = oldHash.toLowerCase()
        const orig = await ethersProvider.getTransaction(oldH)
        if (!orig) return

        const allTransactions = store.getState()?.transactions?.[chainId] ?? {}
        const originalTx = allTransactions[oldH]

        const txReq: TransactionRequest = {
          to: orig.to ?? undefined,
          from: orig.from ?? account,
          data: orig.data,
          nonce: orig.nonce,
          value: orig.value,
          chainId,
        }

        if (orig.maxFeePerGas && orig.maxPriorityFeePerGas) {
          txReq.maxFeePerGas = bump25(orig.maxFeePerGas)
          txReq.maxPriorityFeePerGas = bump25(orig.maxPriorityFeePerGas)
        } else if (orig.gasPrice) {
          txReq.gasPrice = bump25(orig.gasPrice)
        } else {
          const fee = await ethersProvider.getFeeData()
          if (fee.maxFeePerGas && fee.maxPriorityFeePerGas) {
            txReq.maxFeePerGas = bump25(fee.maxFeePerGas)
            txReq.maxPriorityFeePerGas = bump25(fee.maxPriorityFeePerGas)
          } else if (fee.gasPrice) {
            txReq.gasPrice = bump25(fee.gasPrice)
          }
        }

        const sent = await signer.sendTransaction(txReq)

        writeReplacement(account, oldH, sent.hash)

        if (originalTx?.info) {
          dispatch(
            addTransaction({
              hash: sent.hash,
              from: account,
              chainId,
              nonce: orig.nonce,
              info: originalTx.info,
            })
          )
        }

        setReplacements((m) => {
          const nh = sent.hash.toLowerCase()
          const next: Record<string, string> = { ...m, [oldH]: nh }
          for (const [k, v] of Object.entries(next)) {
            if (v.toLowerCase() === oldH) next[k] = nh
          }
          return next
        })
      } catch (err) {
        //console.error('TX speed up failed!', err)
      }
    },
    [ethersProvider, account, chainId, dispatch]
  )

  const activityGroups = useMemo(() => createGroups(displayList), [displayList])

  const onClear = useCallback(() => {
    const now = Date.now()
    writeClearedAt(account, now)
    setClearedAt(now)
  }, [account])

  if (!activityGroups.length) {
    return (
      <PortfolioTabWrapper>
        <EmptyWalletModule type="activity" onNavigateClick={toggleWalletDrawer} />
      </PortfolioTabWrapper>
    )
  }

  return (
    <PortfolioTabWrapper>
      {activityGroups.map((activityGroup, index) => (
        <ActivityGroupWrapper key={activityGroup.title}>
          <GroupHeader>
            <ThemedText.SubHeader color="textSecondary">{activityGroup.title}</ThemedText.SubHeader>
            {index === 0 && activityGroups.length > 0 && <ClearBtn onClick={onClear}>{t`Clear history`}</ClearBtn>}
          </GroupHeader>

          <Column>
            {activityGroup.transactions.map((activity) => (
              <ActivityRow
                key={activity.hash ?? `${activity.timestamp ?? 0}:${activity.title}`}
                activity={activity}
                isSpeedUpCandidate={
                  activity.status === TransactionStatus.Pending &&
                  chainId === 369 &&
                  activity.hash?.toLowerCase() === speedUpCandidateHash
                }
                onSpeedUp={() => onSpeedUp(activity.hash)}
              />
            ))}
          </Column>
        </ActivityGroupWrapper>
      ))}
    </PortfolioTabWrapper>
  )
}
